import type { ApiMaintenanceEvent, ApiMaintenanceSchedule } from '@/api/equipmentApi'
import { CYCLE_LABELS, CYCLE_ORDER, type Cycle } from '@/types/equipment'
import { openEventsForUnit } from '@/lib/storageAvailability'

/**
 * One maintenance task on a storage unit, with every open occurrence of it.
 *
 * Generation writes a MaintenanceEvent per occurrence per unit, so a daily
 * cleaning schedule leaves a unit with dozens of open rows that differ only in
 * their due date (z8nrz7f2fa). People reading the storage unit page want the
 * task, not the rows: what it is, how often, how far behind it is, and when it
 * is next due. The rows stay reachable by expanding the task.
 */
export type UnitMaintenanceTask = {
  /**
   * `equipmentTypeId|eventTypeCode` — the pair generation keys on — for a
   * scheduled task; a one-off adds its own event id.
   */
  key: string
  equipmentTypeName: string
  eventTypeCode: string
  /** Display cadence, e.g. "Daily"; null when nothing says how often it runs. */
  recurrence: string | null
  /** A single piece of work (ad-hoc or calendar) that does not repeat. */
  oneOff: boolean
  /** Overdue occurrences first (oldest first), then upcoming (soonest first). */
  occurrences: ApiMaintenanceEvent[]
  overdueCount: number
  /** `YYYY-MM-DD` of the oldest overdue occurrence, or null when none is. */
  oldestOverdue: string | null
  /** `YYYY-MM-DD` of the soonest occurrence that is not yet overdue. */
  nextDue: string | null
  inProgressCount: number
}

/**
 * The calendar day of a due date as `YYYY-MM-DD`.
 *
 * The type says date-only, but the list feed serialises the `@db.Date` column
 * as a midnight-UTC datetime ("2026-09-28T00:00:00.000Z"). Cutting the string
 * rather than parsing it keeps the viewer's timezone off a value that has none.
 */
export function dueDay(dueDate: string): string {
  return dueDate.slice(0, 10)
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** A due date as `28-Sep-2026`, the unambiguous GMP form; unparseable input is returned as-is. */
export function formatDueDay(dueDate: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dueDate)
  const month = m ? MONTHS[Number(m[2]) - 1] : undefined
  if (!m || !month) return dueDate
  return `${m[3]}-${month}-${m[1]}`
}

/** Event-type code as words, for when the tenant catalog has no label for it. */
export { humanizeEventType } from '@/types/maintenanceTask'

/**
 * How often an event type runs on an equipment type, from that type's schedule.
 *
 * Generated occurrences carry no cadence of their own; the schedule that made
 * them does. One event type can sit on more than one cycle (cleaned daily and
 * deep-cleaned monthly under the same code), and generation keys both onto the
 * same pair, so every cycle that names it is listed, shortest first.
 */
export function scheduleRecurrence(
  schedules: Pick<ApiMaintenanceSchedule, 'cycle' | 'eventTypes'>[],
  eventTypeCode: string,
): string | null {
  const cycles = CYCLE_ORDER.filter((c: Cycle) =>
    schedules.some((s) => s.cycle === c && s.eventTypes.includes(eventTypeCode)),
  )
  return cycles.length ? cycles.map((c) => CYCLE_LABELS[c]).join(' + ') : null
}

function byDue(a: ApiMaintenanceEvent, b: ApiMaintenanceEvent): number {
  return dueDay(a.dueDate).localeCompare(dueDay(b.dueDate))
}

/**
 * Group a unit's open maintenance into one entry per task.
 *
 * `schedulesFor` resolves an equipment type's schedules (empty when unknown).
 * Overdue is the server's flag (tenant timezone + grace), the one the worklist
 * reads, so the counts here cannot disagree with it (z8nrz7dyc7).
 *
 * Tasks that are behind come first, most-behind first; the rest follow by their
 * next due date — the order someone deciding what to deal with would read them.
 */
export function groupUnitMaintenance(
  events: ApiMaintenanceEvent[],
  equipmentId: string | null,
  schedulesFor: (equipmentTypeId: string) => Pick<ApiMaintenanceSchedule, 'cycle' | 'eventTypes'>[] = () => [],
): UnitMaintenanceTask[] {
  const groups = new Map<string, ApiMaintenanceEvent[]>()
  for (const ev of openEventsForUnit(events, equipmentId)) {
    // Only schedule-generated rows are occurrences of one task. A one-off — a
    // corrective clean raised by hand, a calendar date — is its own piece of
    // work, and folding it into the daily cleaning row would label it "Daily"
    // and count its date as that schedule's.
    const key =
      ev.source === 'RECURRING'
        ? `${ev.equipmentTypeId}|${ev.eventTypeCode}`
        : `${ev.equipmentTypeId}|${ev.eventTypeCode}|${ev.id}`
    const list = groups.get(key)
    if (list) list.push(ev)
    else groups.set(key, [ev])
  }

  const tasks: UnitMaintenanceTask[] = []
  for (const [key, list] of groups) {
    const overdue = list.filter((e) => e.overdue).sort(byDue)
    const upcoming = list.filter((e) => !e.overdue).sort(byDue)
    const first = list[0]
    const recurring = first.source === 'RECURRING'
    tasks.push({
      key,
      equipmentTypeName: first.equipmentType?.name ?? 'Maintenance',
      eventTypeCode: first.eventTypeCode,
      recurrence: recurring
        ? scheduleRecurrence(first.equipmentTypeId ? schedulesFor(first.equipmentTypeId) : [], first.eventTypeCode)
        : first.repeatCycle
          ? CYCLE_LABELS[first.repeatCycle]
          : null,
      oneOff: !recurring && !first.repeatCycle,
      occurrences: [...overdue, ...upcoming],
      overdueCount: overdue.length,
      oldestOverdue: overdue[0] ? dueDay(overdue[0].dueDate) : null,
      nextDue: upcoming[0] ? dueDay(upcoming[0].dueDate) : null,
      inProgressCount: list.filter((e) => e.status === 'STARTED').length,
    })
  }

  return tasks.sort((a, b) => {
    if (a.oldestOverdue && b.oldestOverdue) {
      return a.oldestOverdue.localeCompare(b.oldestOverdue) || a.key.localeCompare(b.key)
    }
    if (a.oldestOverdue) return -1
    if (b.oldestOverdue) return 1
    return (a.nextDue ?? '').localeCompare(b.nextDue ?? '') || a.key.localeCompare(b.key)
  })
}
